import { useEffect, useRef, useState } from "react";

import { useClientContextKey } from "@/hooks/useClientContextKey";

import { canAdvanceStep, type resolveFoundPositionPreview, type StepGateState } from "../stepFlowState";
import {
    LAST_STEP,
    type NavSlice,
    nextShownStep,
    previousShownStep,
    REWARDS_DESTINATION_VARIANT,
    STEP_COLLAPSE
} from "../steps";

/** How long a just-completed step holds (its success state reads) before the
 *  flow moves on by itself. */
export const AUTO_ADVANCE_DELAY_MS = 2000;
export function useStepNavigation(
    stepGateState: StepGateState,
    foundPosition: ReturnType<typeof resolveFoundPositionPreview>
): NavSlice {
    const [active, setActive] = useState(0);
    const contextKey = useClientContextKey();
    const previousContextKey = useRef(contextKey);
    useEffect(() => {
        if (previousContextKey.current === contextKey) return;
        previousContextKey.current = contextKey;
        setActive(0);
    }, [contextKey]);
    // Step 0 additionally waits for the preview to render (it holds back
    // until the balance read settles): the user must never advance past the
    // found-position review while the button still reads "Finding...".
    const canAdvance = canAdvanceStep(stepGateState, active, foundPosition);
    const isLast = active === LAST_STEP;

    function handleNext(): void {
        // The last step submits via the in-content button (footer Next is
        // disabled there), so Next only ever advances, skipping a hidden step.
        setActive((prev) => nextShownStep(prev, REWARDS_DESTINATION_VARIANT));
    }

    function handleBack(): void {
        setActive((prev) => previousShownStep(prev, REWARDS_DESTINATION_VARIANT));
    }

    // Auto-advance: when the current step becomes complete (its gate flips
    // from closed to open while the user is on it), hold for
    // AUTO_ADVANCE_DELAY_MS so its success state reads, then move on. Only
    // the flip fires it: arriving at an already-complete step (Back, a
    // jump from the rail) never bounces the user forward again. Leaving the
    // step first (Next, Back) cancels the pending move.
    const gateRef = useRef({ active, canAdvance });
    const [autoAdvanceFrom, setAutoAdvanceFrom] = useState<number | null>(null);
    useEffect(() => {
        const previous = gateRef.current;
        gateRef.current = { active, canAdvance };
        if (previous.active !== active) {
            setAutoAdvanceFrom(null);
            return;
        }
        if (!previous.canAdvance && canAdvance && !isLast) setAutoAdvanceFrom(active);
        else if (!canAdvance) setAutoAdvanceFrom(null);
    }, [active, canAdvance, isLast]);
    useEffect(() => {
        if (autoAdvanceFrom === null) return;
        const timer = window.setTimeout(() => {
            setAutoAdvanceFrom(null);
            setActive((prev) => (prev === autoAdvanceFrom ? nextShownStep(prev, REWARDS_DESTINATION_VARIANT) : prev));
        }, AUTO_ADVANCE_DELAY_MS);
        return (): void => window.clearTimeout(timer);
    }, [autoAdvanceFrom]);

    // Reveal the newly-active step only after the previous one finishes
    // collapsing. Track the prior `active` so we know we just advanced (and from
    // where): leaving the surface step waits for its longer cascade, other steps
    // wait for the shorter summary roll-up. Going back is instant.
    const prevActiveRef = useRef(active);
    const advancing = active > prevActiveRef.current;
    const revealDelay = !advancing
        ? 0
        : prevActiveRef.current <= 1
          ? // Steps 0–1 both collapse the same way — a height-collapse over
            // ~moveDuration (the field/section folds away, the chosen row stays
            // and rises). Wait that out before revealing the next step.
            STEP_COLLAPSE.moveDuration
          : 0.3;
    useEffect(() => {
        prevActiveRef.current = active;
    }, [active]);

    return { active, setActive, canAdvance, isLast, onNext: handleNext, onBack: handleBack, revealDelay };
}
